import { ref } from "vue";
import { AwsRum } from "aws-rum-web";
import { restClient } from "@/lib/raftApi";
import { useMemoize } from "@vueuse/core";
import { useDrupalSettings } from "./useDrupalSettings";

/**
 * useCloudWatchRum - Vue composable for integrating AWS CloudWatch RUM
 */
// eslint-disable-next-line import/prefer-default-export
export function useCloudWatchRum() {
  const awsRum = ref(null);
  const { config } = useDrupalSettings();

  // AwsRum's "disableAutoPageView" is set to false by default,
  // so page views are already automatically recorded.
  // This function allows us to add custom metadata to the page view.
  const recordMetadata = (pageAttributes) => {
    try {
      awsRum.value.recordPageView({
        pageId: window.location.pathname,
        pageAttributes,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error recording metadata:", error);
    }
  };

  const initializeRum = useMemoize(async () => {
    const notAdmin = config?.currentUser && parseInt(config.currentUser) > 1;
    if (notAdmin) {
      try {
        await restClient
          .get({ path: "/performance-config", queries: {} })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error("Error fetching performance config:", error);
            return null;
          })
          .then((data) => {
            const rumConfig = {
              sessionSampleRate: data.sessionSampleRate,
              identityPoolId: data.identityPoolId,
              endpoint: data.endpoint,
              telemetries: data.telemetries,
              allowCookies: data.allowCookies,
              enableXRay: data.enableXRay,
              signing: data.signing,
            };
            const rumInstance = new AwsRum(
              data.APPLICATION_ID,
              data.APPLICATION_VERSION,
              data.APPLICATION_REGION,
              rumConfig,
            );
            awsRum.value = rumInstance;
            return awsRum;
          });
        if (config.nodeType) {
          recordMetadata({ nodeType: config.nodeType });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error initializing AWS RUM:", error);
      }
    }
  });

  return {
    awsRum,
    initializeRum,
    recordMetadata,
  };
}
